import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { adminResourceHubDeleteAction, adminResourceHubListAction } from "../Action/action";
import config from "../coreFIles/config";
import DeleteConfirmModal from "./delete-confirm-modal";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function ResourceHub() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminResourceHubListAction();
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || res?.msg || "Failed to load resource hub.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load resource hub.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  const viewRows = useMemo(() => {
    const mapped = (Array.isArray(rows) ? rows : []).map((r) => {
      const type = String(r?.type || "resource_hub").toLowerCase() === "how_to" ? "how_to" : "resource_hub";
      return {
        raw: r,
        id: r?.id,
        title: r?.title || "-",
        type,
        typeLabel: type === "how_to" ? "How To" : "Resource Hub",
        thumbnail: r?.thumbnail ? resolvePublicUrl(r.thumbnail) : "",
        pdfFile: r?.pdf_file ? resolvePublicUrl(r.pdf_file) : "",
        sortOrder: Number.isFinite(Number(r?.sort_order)) ? Number(r.sort_order) : 0,
        status: r?.status !== undefined && r?.status !== null ? !!Number(r.status) : true,
        created: formatDate(r?.created_at)
      };
    });
    if (typeFilter === "how_to" || typeFilter === "resource_hub") {
      return mapped.filter((r) => r.type === typeFilter);
    }
    return mapped;
  }, [rows, typeFilter]);

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminResourceHubDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Resource hub item disabled", { toastId: "resource-hub-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", { toastId: "resource-hub-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">!</span>
          <div className="banner-text">
            <strong>Resource Hub unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <h3 style={{ margin: 0 }}>Resource Hub</h3>
          <select className="form-control" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 180 }}>
            <option value="all">All types</option>
            <option value="resource_hub">Resource Hub</option>
            <option value="how_to">How To</option>
          </select>
        </div>
        <Link to="/resource-hub/new" className="btn btn-primary">
          Add Resource
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Title</th>
                <th>Type</th>
                <th>PDF</th>
                <th>Sort Order</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.thumbnail ? (
                        <img
                          src={r.thumbnail}
                          alt={r.title}
                          style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 8 }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>
                      <span className={`badge ${r.type === "how_to" ? "badge-gray" : "badge-success"}`}>
                        {r.typeLabel}
                      </span>
                    </td>
                    <td>
                      {r.pdfFile ? (
                        <a href={r.pdfFile} target="_blank" rel="noreferrer" className="action-link">
                          View PDF
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{r.sortOrder}</td>
                    <td>
                      <span className={`badge ${r.status ? "badge-success" : "badge-gray"}`}>
                        {r.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/resource-hub/${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      No resource hub items yet.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="resource hub item"
        itemName={deleteRow?.title || "Resource Hub Item"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
